/**
 * useQuickGuaranteeScore - Fetches instant guarantee score for a scenario.
 *
 * Calls GET /api/site_dna/v1/guarantee/quick-score with project + scenario IDs.
 * Backend builds the SiteProfile features from optimization results and returns
 * P50/P75/P90 savings bands + risk grade.
 *
 * Only fires when optimization results exist (hasResults = true).
 */

import { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { API_BASE_URL } from '@vortx/shared-ui';

export function useQuickGuaranteeScore(projectId, scenarioId, hasResults) {
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId || !scenarioId || !hasResults) {
      setScoreData(null);
      return;
    }

    let cancelled = false;

    async function fetchScore() {
      setLoading(true);
      setError(null);

      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) {
          throw new Error('Not authenticated');
        }
        const token = await user.getIdToken();

        const params = new URLSearchParams({ project_id: projectId, scenario_id: scenarioId });
        const res = await fetch(
          `${API_BASE_URL}/api/site_dna/v1/guarantee/quick-score?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || `Scoring failed (${res.status})`);
        }

        const data = await res.json();
        if (!cancelled) {
          setScoreData(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchScore();

    return () => {
      cancelled = true;
    };
  }, [projectId, scenarioId, hasResults]);

  return { scoreData, loading, error };
}
